import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CircleDollarSign, RefreshCw } from "lucide-react";
import {
  createDistributorWithdrawal,
  fetchDistributorProfile,
  fetchDistributorWithdrawals,
  type ApiError,
  type DistributorProfile,
  type DistributorWithdrawal,
} from "@/api/member";
import { clearDistributorSession, ensureDistributorAccessToken } from "@/utils/distributorSession";

function formatMoney(value?: number) {
  return `¥${((value || 0) / 100).toFixed(2)}`;
}

function formatDateTime(value?: string) {
  if (!value) {
    return "-";
  }
  return String(value).replace("T", " ").slice(0, 19);
}

const statusText: Record<string, string> = {
  pending: "待处理",
  approved: "已通过",
  rejected: "已拒绝",
};

const methodText: Record<string, string> = {
  alipay: "支付宝",
  wechat: "微信",
  bank: "银行卡",
  usdt: "USDT",
};

export default function DistributorWithdrawPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<DistributorProfile | null>(null);
  const [records, setRecords] = useState<DistributorWithdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("alipay");
  const [account, setAccount] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await ensureDistributorAccessToken();
      if (!token) {
        navigate("/distributor/login", { replace: true });
        return;
      }
      const [profileData, withdrawalData] = await Promise.all([
        fetchDistributorProfile(token),
        fetchDistributorWithdrawals(token, { pageNum: 1, pageSize: 30 }),
      ]);
      setProfile(profileData);
      setRecords(withdrawalData.list || []);
    } catch (err) {
      setError((err as ApiError).message || "提现数据加载失败");
      if ((err as ApiError).status === 401) {
        clearDistributorSession();
        navigate("/distributor/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const submitWithdrawal = async () => {
    setError("");
    const amountFen = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountFen) || amountFen <= 0) {
      setError("请输入有效的提现金额");
      return;
    }
    if (amountFen < 10000) {
      setError("最低提现金额为 ¥100.00");
      return;
    }
    if (profile && amountFen > profile.availableBalance) {
      setError("提现金额不能超过可提现余额");
      return;
    }
    if (!account.trim()) {
      setError("请输入收款账号");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureDistributorAccessToken();
      if (!token) {
        navigate("/distributor/login", { replace: true });
        return;
      }
      await createDistributorWithdrawal(token, {
        amount: amountFen,
        method,
        account: account.trim(),
      });
      setShowModal(false);
      setAmount("");
      setAccount("");
      setToast("提现申请已提交");
      await loadData();
    } catch (err) {
      setError((err as ApiError).message || "提现申请失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-800 text-white">
      <div className="sticky top-0 z-30 border-b border-dark-300/60 bg-dark-800/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Link
              to="/distributor/dashboard"
              className="flex h-9 w-9 items-center justify-center rounded-full text-white/55 hover:text-white"
            >
              <ArrowLeft size={20} />
            </Link>
            <span className="font-bold">提现处理</span>
          </div>
          <button
            type="button"
            onClick={() => void loadData()}
            className="inline-flex h-9 items-center gap-2 rounded-button border border-dark-300 bg-dark-500 px-3 text-sm text-white/70 hover:text-white"
          >
            <RefreshCw size={16} />
            刷新
          </button>
        </div>
      </div>

      <main className="mx-auto flex max-w-5xl flex-col gap-5 px-4 py-6 md:px-6">
        {error && <div className="rounded-card border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}
        {toast && <div className="rounded-card border border-mint-400/30 bg-mint-400/10 p-3 text-sm text-mint-200">{toast}</div>}

        <section className="rounded-2xl border border-dark-300 bg-dark-500 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-white/40">可提现余额</p>
              <p className="mt-2 text-4xl font-black text-mint-400">{formatMoney(profile?.availableBalance)}</p>
              <p className="mt-2 text-xs text-white/35">
                累计佣金 {formatMoney(profile?.totalCommission)}，处理中 {formatMoney(profile?.pendingWithdrawAmount)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setError("");
                setShowModal(true);
              }}
              disabled={loading}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-button bg-[#F97316] px-6 font-bold text-dark-900 hover:bg-[#F97316]/90 disabled:opacity-50"
            >
              <CircleDollarSign size={20} />
              发起提现
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-dark-300 bg-dark-500 p-5">
          <h2 className="mb-4 font-bold">提现记录</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] text-sm">
              <thead>
                <tr className="border-b border-dark-300 text-xs text-white/45">
                  <th className="px-3 py-3 text-left">提现单号</th>
                  <th className="px-3 py-3 text-right">金额</th>
                  <th className="px-3 py-3 text-center">方式</th>
                  <th className="px-3 py-3 text-left">账号</th>
                  <th className="px-3 py-3 text-center">状态</th>
                  <th className="px-3 py-3 text-left">备注</th>
                  <th className="px-3 py-3 text-right">申请时间</th>
                </tr>
              </thead>
              <tbody>
                {records.map((item) => (
                  <tr key={item.id} className="border-b border-dark-300/50">
                    <td className="px-3 py-3 font-mono text-xs text-white/65">{item.withdrawNo}</td>
                    <td className="px-3 py-3 text-right font-bold">{formatMoney(item.amount)}</td>
                    <td className="px-3 py-3 text-center text-white/70">{methodText[item.method] || item.method}</td>
                    <td className="px-3 py-3 text-white/70">{item.account}</td>
                    <td className="px-3 py-3 text-center">
                      <span
                        className={`rounded-full border px-2.5 py-1 text-xs ${
                          item.status === "approved"
                            ? "border-mint-400/30 bg-mint-400/10 text-mint-400"
                            : item.status === "rejected"
                              ? "border-red-400/30 bg-red-400/10 text-red-300"
                              : "border-gold-400/30 bg-gold-400/10 text-gold-400"
                        }`}
                      >
                        {statusText[item.status] || item.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-white/55">{item.remark || "-"}</td>
                    <td className="px-3 py-3 text-right text-white/45">{formatDateTime(item.createdAt)}</td>
                  </tr>
                ))}
                {!loading && records.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-white/35">
                      暂无提现记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-dark-300 bg-dark-500 p-5 shadow-2xl">
            <h3 className="text-lg font-bold">申请提现</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs text-white/45">提现金额</label>
                <input
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="请输入金额"
                  className="h-11 w-full rounded-button border border-dark-300 bg-dark-700 px-3 text-sm outline-none focus:border-mint-400"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/45">提现方式</label>
                <select
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                  className="h-11 w-full rounded-button border border-dark-300 bg-dark-700 px-3 text-sm outline-none focus:border-mint-400"
                >
                  <option value="alipay">支付宝</option>
                  <option value="wechat">微信</option>
                  <option value="bank">银行卡</option>
                  <option value="usdt">USDT</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/45">收款账号</label>
                <input
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  placeholder="请输入收款账号"
                  className="h-11 w-full rounded-button border border-dark-300 bg-dark-700 px-3 text-sm outline-none focus:border-mint-400"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="h-10 rounded-button border border-dark-300 px-4 text-sm text-white/70 hover:text-white"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void submitWithdrawal()}
                disabled={submitting}
                className="h-10 rounded-button bg-[#F97316] px-4 text-sm font-bold text-dark-900 hover:bg-[#F97316]/90 disabled:opacity-50"
              >
                提交
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
